import LegalLayout from '@/components/feature/LegalLayout';
import { useTranslation } from 'react-i18next';

export default function TermsOfService() {
  const { t } = useTranslation();
  return (
    <LegalLayout title={t('terms.title', 'Terms of Service')} lastUpdated="May 20, 2026">
      <div className="space-y-8">
        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s1', '1. Acceptance of Terms')}</h2>
          <p className="leading-relaxed mb-3">
            By accessing or using the ZhiSu platform, website, mobile applications, APIs, or any related services (collectively, the &quot;Services&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, you must not access or use the Services.
          </p>
          <p className="leading-relaxed">
            These Terms constitute a legally binding agreement between you and ZhiSu Inc. (&quot;ZhiSu&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). Please read them carefully before using our Services.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s2', '2. Eligibility')}</h2>
          <p className="leading-relaxed mb-3">
            You must be at least 18 years old and have the legal capacity to enter into these Terms. By using the Services, you represent and warrant that:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>You are not a resident of, or located in, any jurisdiction where the use of the Services is prohibited by applicable law.</li>
            <li>You are not on any sanctions list maintained by the United Nations, the U.S. Department of Treasury, the European Union, or any other applicable authority.</li>
            <li>You are not a citizen or resident of the United States, Canada, China, or any other jurisdiction where perpetual derivatives trading is restricted.</li>
            <li>You will use the Services in compliance with all applicable laws, regulations, and these Terms.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s3', '3. Description of Services')}</h2>
          <p className="leading-relaxed mb-3">
            ZhiSu provides a decentralized trading platform that enables users to engage in perpetual futures trading, spot trading, and other financial instruments on a non-custodial basis. Our Services include:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Perpetual Trading:</strong> Leveraged perpetual futures contracts on various cryptocurrency pairs.</li>
            <li><strong>AI Trading Agents:</strong> Automated trading strategies powered by artificial intelligence.</li>
            <li><strong>Prediction Markets:</strong> Decentralized markets for speculating on future events.</li>
            <li><strong>Copy Trading:</strong> Ability to replicate trades of other users or AI agents.</li>
            <li><strong>API Access:</strong> Programmatic access to market data, order management, and account functions.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s4', '4. Account Registration and Security')}</h2>
          <p className="leading-relaxed mb-3">
            To access certain features of the Services, you may need to connect a compatible cryptocurrency wallet. You are solely responsible for:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Maintaining the confidentiality of your wallet private keys, seed phrases, and any other credentials.</li>
            <li>All activities that occur under your wallet address, whether or not authorized by you.</li>
            <li>Immediately notifying us of any unauthorized use of your account or any other security breach.</li>
          </ul>
          <p className="leading-relaxed mt-3">
            We are not responsible for any loss of assets resulting from your failure to maintain the security of your wallet credentials.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s5', '5. Trading Rules and Risks')}</h2>
          <p className="leading-relaxed mb-3">
            You acknowledge and agree that:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Trading cryptocurrency derivatives involves substantial risk of loss and is not suitable for all investors.</li>
            <li>The use of leverage amplifies both potential gains and potential losses.</li>
            <li>Past performance of any trading strategy, including AI agents, does not guarantee future results.</li>
            <li>Market conditions can change rapidly, and your positions may be liquidated if margin requirements are not met.</li>
            <li>You are solely responsible for your trading decisions and their outcomes.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s6', '6. Fees and Charges')}</h2>
          <p className="leading-relaxed mb-3">
            ZhiSu charges fees for certain transactions and services. All applicable fees are displayed on our Fees page and are subject to change with reasonable notice. Fees may include:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Trading fees (maker and taker fees)</li>
            <li>Funding fees for perpetual positions</li>
            <li>Withdrawal fees for on-chain transactions</li>
            <li>Subscription fees for premium AI agents or data services</li>
          </ul>
          <p className="leading-relaxed mt-3">
            All fees are denominated in the relevant cryptocurrency and are non-refundable except as expressly provided in these Terms or required by applicable law.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s7', '7. Prohibited Activities')}</h2>
          <p className="leading-relaxed mb-3">
            You agree not to engage in any of the following prohibited activities:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Using the Services for money laundering, terrorist financing, or any other illegal activity.</li>
            <li>Manipulating markets, including wash trading, spoofing, or layering.</li>
            <li>Exploiting bugs, glitches, or vulnerabilities in the platform for personal gain.</li>
            <li>Using bots, scripts, or automated systems in violation of our API terms.</li>
            <li>Impersonating any person or entity, or misrepresenting your affiliation with any person or entity.</li>
            <li>Attempting to interfere with the proper functioning of the Services.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s8', '8. Intellectual Property')}</h2>
          <p className="leading-relaxed">
            All content, trademarks, logos, software, and other intellectual property displayed on the Services are owned by ZhiSu or licensed to us. You may not copy, modify, distribute, sell, or lease any part of our Services or included software without our express written permission. The ZhiSu name, logo, and related marks are registered trademarks.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s9', '9. Disclaimer of Warranties')}</h2>
          <p className="leading-relaxed">
            THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s10', '10. Limitation of Liability')}</h2>
          <p className="leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, ZhiSu PROTOCOL AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR ACCESS TO OR USE OF THE SERVICES.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s11', '11. Indemnification')}</h2>
          <p className="leading-relaxed">
            You agree to indemnify, defend, and hold harmless ZhiSu and its affiliates from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys&apos; fees) arising out of or relating to your use of the Services, your violation of these Terms, or your violation of any rights of another party.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s12', '12. Governing Law and Dispute Resolution')}</h2>
          <p className="leading-relaxed mb-3">
            These Terms shall be governed by and construed in accordance with the laws of the British Virgin Islands, without regard to its conflict of law provisions.
          </p>
          <p className="leading-relaxed">
            Any dispute arising out of or relating to these Terms or the Services shall first be attempted to be resolved through good faith negotiations. If negotiations fail, the dispute shall be finally resolved by binding arbitration in accordance with the rules of the London Court of International Arbitration (LCIA).
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s13', '13. Amendments')}</h2>
          <p className="leading-relaxed">
            We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the Services. Your continued use of the Services after any changes constitutes your acceptance of the revised Terms. Material changes will be notified through prominent posting on our website or via email if you have provided contact information.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('terms.s14', '14. Contact Information')}</h2>
          <p className="leading-relaxed">
            If you have any questions about these Terms, please contact us at legal@zhisu.trade or through our Support Center. Our mailing address is ZhiSu Inc., P.O. Box 3174, Road Town, Tortola, British Virgin Islands.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}